import { createContext, useCallback, useContext, useMemo, useReducer } from 'react'
import * as orderService from '../services/orderService'
import { getUserFacingErrorMessage } from '../utils/userFacingError'
import { OrderActionType, createInitialOrderState, orderReducer } from './reducers/orderReducer'

const OrderContext = createContext<any>(null)

export function OrderProvider({ children }) {
  const [state, dispatch] = useReducer(orderReducer, undefined, createInitialOrderState)

  const fetchOrders = useCallback(
    async (params: orderService.OrdersListParams = {}, opts: { totalsOnly?: boolean } = {}) => {
      if (!opts.totalsOnly) dispatch({ type: OrderActionType.LOAD_LIST_START })
      try {
        const data = await orderService.fetchOrders(params)
        if (opts.totalsOnly) {
          dispatch({ type: OrderActionType.SET_LIST_TOTAL, payload: data.total })
        } else {
          dispatch({ type: OrderActionType.LOAD_LIST_SUCCESS, payload: data })
        }
        return data
      } catch (e) {
        dispatch({
          type: OrderActionType.LOAD_LIST_FAILURE,
          payload: getUserFacingErrorMessage(e, 'Failed to load orders'),
        })
        throw e
      }
    },
    [],
  )

  const getOrderByReference = useCallback(async (orderReference: string) => {
    dispatch({ type: OrderActionType.SET_SELECTED, payload: null })
    try {
      const item = await orderService.fetchOrderByReference(orderReference)
      dispatch({ type: OrderActionType.SET_SELECTED, payload: item })
      return item
    } catch (e) {
      dispatch({
        type: OrderActionType.SET_ERROR,
        payload: getUserFacingErrorMessage(e, 'Failed to load order'),
      })
      throw e
    }
  }, [])

  const updateOrderStatus = useCallback(async (orderReference: string, status: string) => {
    dispatch({ type: OrderActionType.UPDATE_STATUS_START })
    try {
      const updated = await orderService.updateOrderStatus(orderReference, status)
      dispatch({ type: OrderActionType.UPDATE_STATUS_SUCCESS, payload: updated })
      return updated
    } catch (e) {
      dispatch({
        type: OrderActionType.SET_ERROR,
        payload: getUserFacingErrorMessage(e, 'Failed to update order'),
      })
      throw e
    }
  }, [])

  const clearError = useCallback(() => {
    dispatch({ type: OrderActionType.CLEAR_ERROR })
  }, [])

  const value = useMemo(
    () => ({
      orders: state.orders,
      listTotal: state.listTotal,
      listPage: state.listPage,
      listLimit: state.listLimit,
      selectedOrder: state.selectedOrder,
      listLoading: state.listLoading,
      mutationLoading: state.mutationLoading,
      error: state.error,
      fetchOrders,
      getOrderByReference,
      updateOrderStatus,
      clearError,
    }),
    [state, fetchOrders, getOrderByReference, updateOrderStatus, clearError],
  )

  return <OrderContext.Provider value={value}>{children}</OrderContext.Provider>
}

export function useOrder() {
  const ctx = useContext(OrderContext)
  if (!ctx) throw new Error('useOrder must be used within OrderProvider')
  return ctx
}
